import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import ReactPaginate from "react-paginate";
import { useTranslation } from "react-i18next";
import { useRolePermission } from "../../../Hooks/useRolePermission";
import { useDispatch, useSelector } from "react-redux";
import MaterialSelect from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
// component
import ButtonElement from "../../UI/Button";
import SwitchElement from "../../UI/Switch";
import { getTableSkeletons } from "../../UI/Skeleton/Loader";
//helper
import { getLocalStorage } from "../../../Network/ApiService";
import { ls_vars } from "../../../Library/Constants";
import { pageOptions } from "../../../StaticData/UserManageMent/status";
import { scrollBar } from "../../Common/Header/ManageOutsideClickClose";
import { handleError } from "../../../Library/Utils";
//API
import {
  approveEmission,
  copyEquipmentMaster,
  deleteEquipment,
  getEquipmentList,
  getEquipmentMasterList,
  rejectEmission,
  updateEquipment,
} from "../../../Network/Core/EquipmentMaster/equipment";
//redux
import { setSearch } from "../../../Store/Actions/EquipmentMaster/equipmentMasterActionCreatore";
import { setLoader } from "../../../Store/Actions/Loader/LoaderActionCreator";
//images
import images from "../../../Assets/images";
// css
// import "../../../Assets/css/style.css";
import "../../../Assets/css/customize.css";
import "./style.css";
import { useApiCall } from "Hooks/useApiCall";
import {
  EquipmentType,
  GetEquipmentListParams,
} from "Network/Core/EquipmentMaster/types";
import debounce from "lodash/debounce";
import EquipmentMasterModal from "Components/Common/Modal/EquipmentMasterModal";
import { id } from "date-fns/locale";
import DeleteConfirmationModal from "Components/Common/Modal/DeleteConfirmationModal";
import { getFilteredCommonUtils } from "Network/Core/CommonUtils/commonUtils";
import Pagination from "Components/UI/Pagination/Pagination";
import { useForm } from "react-hook-form";
import MasterCopyModel from "Components/Common/Modal/CopyFromMasterModel";

const index = () => {
  const { t } = useTranslation();
  const { call } = useApiCall();
  const { checkPermission } = useRolePermission();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const project = getLocalStorage(ls_vars.PROJECT_DATA);
  const site = getLocalStorage(ls_vars.PROJECT_SITE_ID);
  const region = getLocalStorage(ls_vars.REGION_ID);
  const dateFormate = getLocalStorage(ls_vars.USER_PREFERENCES)?.dateFormat?.id;

  const isLoading = useSelector((state: any) => state.loader.loading);
  //   const search = useSelector((state: any) => state.equipment.search);

  const [orderBy, setOrderBy] = useState<string>("name");
  const [equipmentData, setEquipmentData] = useState<EquipmentType[]>([]);
  const [statusIsASC, setStatusIsASC] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<string>("");
  const [pageCount, setPageCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const [isEquipmentModalOpen, setIsEquipmentModalOpen] =
    useState<boolean>(false);
  const [isConfirmModalShow, setIsConfirmModalShow] = useState<boolean>(false);
  const [existingData, setExistingData] = useState<EquipmentType>(
    {} as EquipmentType
  );
  const [equipmentToBeDeleted, setEquipmentToBeDeleted] = useState<any>();
  const [fuelTypeOptions, setFuelTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [categoryOptions, setCategoryOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [isISEquipmentCopyModalOpen, setISEquipmentCopyModalOpen] =
    useState<boolean>(false);
  const [isGlobalMasterData, setIsGlobalMasterData] = useState<any>([]);
  const canViewEquipment = checkPermission(["equipment:view"]);
  const canCreateEquipment = checkPermission(["equipment:create"]);
  const canUpdateEquipment = checkPermission(["equipment:update"]);
  const canDeleteEquipment = checkPermission(["equipment:delete"]);

  scrollBar(setIsScrolling);

  const {
    register, // this use for events and value
    handleSubmit, // this is form submit handle
    setError,
    control,
    setValue,
    getValues,
    formState: { errors },
    clearErrors,
  } = useForm();

  const handlePageClick = (data: { selected: number }) => {
    dispatch(setLoader(true));
    const { selected } = data;
    setPage(selected + 1);
  };

  const onChangePage = (e: { value: number }) => {
    setPerPage(e.value);
  };

  // Debounce search value changes
  const debouncedSearchValue = useRef(
    debounce((value: string) => {
      dispatch(setSearch(value));
    }, 500)
  ).current;

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearchValue(value.trim());
  };

  const getEquipmentDetails = useCallback(async () => {
    dispatch(setLoader(true));

    try {
      let localPageCount = page;
      if (searchValue && page !== 1) {
        localPageCount = 1;
        setPage(1);
      }

      const params: GetEquipmentListParams = {
        page: localPageCount,
        pageSize: perPage,
        search: searchValue.trim(),
        orderBy: orderBy,
      };

      const res = await getEquipmentList(params, region, project.ref, site);

      const { status, data, statusText } = res;
      switch (status) {
        case 200:
          setEquipmentData(data?.results);
          setPageCount(Math.ceil(data.count / perPage));
          break;
        case 400:
          handleError(data, t);
          break;
        default:
          toast.error(statusText);
          break;
      }
    } catch (err) {
      toast.error(t("Translation.error_something_went_wrong"));
    } finally {
      dispatch(setLoader(false));
    }
  }, [dispatch, page, perPage, orderBy, searchValue, t]); // Add all dependencies

  useEffect(() => {
    getFuelTypes();
    getCategories();
    getEquipmentDetails();
    CopyModelData();
  }, [getEquipmentDetails]); // Now we only need getEquipmentDetails as a dependency

  const getFuelTypes = async () => {
    call(
      () => getFilteredCommonUtils("FUEL_TYPE", region),
      (data) => {
        const options = data?.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        const sortedOptions = options?.sort((a: any, b: any) =>
          a.label.localeCompare(b.label)
        );
        setFuelTypeOptions(sortedOptions);
      }
    );
  };

  const getCategories = async () => {
    call(
      () => getFilteredCommonUtils("EQUIPMENT_CATEGORY", region),
      (data) => {
        const options = data?.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        const sortedOptions = options?.sort((a: any, b: any) =>
          a.label.localeCompare(b.label)
        );
        setCategoryOptions(sortedOptions);
      }
    );
  };

  const toggleSort = (field: string, isAcs: boolean) => {
    if (field !== "id") {
      setStatusIsASC(!isAcs);
      setOrderBy(isAcs ? field : "-" + field);
    }
  };

  const handleAddEquipment = () => {
    setExistingData({} as EquipmentType);
    setIsEquipmentModalOpen(true);
  };

  const handleCopyEquipment = () => {
    setISEquipmentCopyModalOpen(true);
  };
  const handleCopyEquipmentSubmit = (selectedItems: any) => {
    const payload = {
      equipment_id: selectedItems?.map((item: any) => item.value),
      site: site,
    };
    call(
      () => copyEquipmentMaster(payload, region),
      (res) => {
        setISEquipmentCopyModalOpen(false);
        getEquipmentDetails();
      },
      { successMessage: "Translation.equipment_master_copy_success" }
    );
  };

  const handleEquipmentCopyModalClose = () => {
    setISEquipmentCopyModalOpen(false);
    getEquipmentDetails();
  };

  const handleEquipmentModalClose = () => {
    setIsEquipmentModalOpen(false);
    getEquipmentDetails();
  };

  const handleDeleteEquipment = async (equipment: any) => {
    if (!equipment || equipment.id === undefined) {
      console.error("Cannot delete equipment: Invalid ID");
      return;
    }
    call(
      () => deleteEquipment(equipment.id, region, project.ref),
      (res: any) => {
        closeConfirmationModal();
        if (equipmentData.length === 1 && page > 1) {
          setPage(page - 1);
        } else {
          getEquipmentDetails();
        }
      },
      {
        successMessage: "Translation.equipment_master_delete_success",
        onError: (error) => {
          if (error?.error) toast.error(t(`Translation.${error.error}`));
          else if (error) toast.error(t(`Translation.${error}`));
        },
      }
    );
  };

  const handleUpdateEquipmentSubmit = async (equipment: any) => {
    if (!equipment || equipment.id === undefined) {
      console.error("Cannot update equipment: Invalid ID");
      return;
    }

    const id = equipment.id;
    call(
      () => updateEquipment(id, equipment, region, project.ref, site),
      (data) => {
        // Update the state with the response data
        setEquipmentData((prevData) =>
          prevData.map((item) => (item.id === id ? data : item))
        );
      },
      { successMessage: "Translation.equipment_master_edit_success" }
    );
  };

  const closeConfirmationModal = () => {
    setIsConfirmModalShow(false);
  };

  const handleActiveToggle = async (equipment: any) => {
    // Create a new object with the updated isActive status
    const updatedEquipment = { ...equipment, isActive: !equipment.isActive };

    // Update the state
    setEquipmentData((prevData) =>
      prevData.map((item) =>
        item.id === equipment.id ? updatedEquipment : item
      )
    );
    await handleUpdateEquipmentSubmit(updatedEquipment);
  };
  const CopyModelData = () => {
    getEquipmentMasterList(region, project.ref, site)
      .then((res) => {
        const equipments = res.map((item: any) => ({
          label: `${item.name}-${item.brand}-${item.model}-${item.fuelType}`,
          value: item.id,
        }));
        setIsGlobalMasterData(equipments || []);
      })
      .catch((err) => {
        console.error("Equipment master list fetch failed:", err);
      });
  };

  const handleApproveClick = (equipment: EquipmentType) => {
    call(
      () => approveEmission(equipment?.id || 0, region, project.ref),
      (res) => {
        getEquipmentDetails();
      }
    );
  };

  const handleRejectClick = (equipment: EquipmentType) => {
    call(
      () => rejectEmission(equipment?.id || 0, region, project.ref),
      (res) => {
        getEquipmentDetails();
      }
    );
  };

  return (
    <>
      {isEquipmentModalOpen && (
        <>
          <EquipmentMasterModal
            handleClose={() => handleEquipmentModalClose()}
            existingData={existingData}
            categoryOptions={categoryOptions}
            fuelTypeOptions={fuelTypeOptions}
          />
          <div className="modal-backdrop fade show" />
        </>
      )}
      {isISEquipmentCopyModalOpen && (
        <>
          <MasterCopyModel
            handleSubmit={handleCopyEquipmentSubmit}
            handleClose={() => handleEquipmentCopyModalClose()}
            data={isGlobalMasterData}
          />
        </>
      )}
      {isConfirmModalShow && (
        <DeleteConfirmationModal
          isOpen={isConfirmModalShow}
          contentName={"Equipment"}
          deleteContent={"EquipmentDelete"}
          closeModal={closeConfirmationModal}
          onConfirmAction={() => handleDeleteEquipment(equipmentToBeDeleted)}
        />
      )}

      <div className="page-wrapper pb-0">
        <div className="page-header">
          <h3 className="page-title">{t("Translation.equipment_master")}</h3>
        </div>

        <div className="page-content custom-list-div">
          <div className="user-head-sec">
            <div className="booking-list-form">
              <div className="filter-wrapper">
                <div className="f-left">
                  <div className="form-group form-inline filter-search ml-0">
                    <img src={images.search} />
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("Translation.search")}
                      value={searchValue}
                      onChange={handleSearchChange}
                    />
                  </div>

                  {canCreateEquipment && (
                    <ButtonElement
                      buttonLabel={t("Translation.equipment_master_add")}
                      handleClick={handleAddEquipment}
                      className="btn theme-btn invite-btn"
                    />
                  )}
                  <ButtonElement
                    buttonLabel={t("Translation.equipment_master_copy")}
                    handleClick={handleCopyEquipment}
                    className="btn theme-btn invite-btn m-1"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="booking-list-wrapper">
            <div
              className={`booking-list-data scroll ${
                isScrolling ? "show-scroll" : "no-scroll"
              }`}
              style={{ flex: "1 1 0%", overflow: "auto", padding: "0px 24px 24px" }}
            >
              <div className="tab-content" id="pills-tabContent">
                <div
                  className="tab-pane fade show active"
                  id="pills-all"
                  role="tabpanel"
                  aria-labelledby="pills-all-tab"
                >
                  <div className="table-responsive">
                    {isLoading ? (
                      getTableSkeletons({
                        cols: 6,
                        rows: 3,
                        props: {
                          TableRowProps: {
                            style: { height: "75px" },
                          },
                        },
                      })
                    ) : (
                      <table className="display nowrap dataTable w-100">
                        <thead>
                          <tr>
                            <th
                              className="sorting sorting_asc"
                              onClick={() => toggleSort("name", statusIsASC)}
                            >
                              <span>{`${t("Translation.equipment_name")}`}</span>
                            </th>
                            <th
                              className="sorting"
                              onClick={() =>
                                toggleSort("category__name", statusIsASC)
                              }
                            >
                              <span>{`${t("Translation.equipment_category")}`}</span>
                            </th>
                            <th
                              className="sorting"
                              onClick={() => toggleSort("brand", statusIsASC)}
                            >
                              <span>{`${t("Translation.equipment_brand")}`}</span>
                            </th>
                            <th
                              className="sorting"
                              onClick={() => toggleSort("model", statusIsASC)}
                            >
                              <span>{`${t("Translation.equipment_model")}`}</span>
                            </th>
                            <th
                              className="sorting"
                              onClick={() =>
                                toggleSort("fuel_type", statusIsASC)
                              }
                            >
                              <span>{`${t(
                                "Translation.fuel_type"
                              )}`}</span>
                            </th>
                            <th
                              className="sorting"
                              onClick={() =>
                                toggleSort("emission_factor", statusIsASC)
                              }
                            >
                              <span>{`${t("Translation.emission_factor")}`}</span>
                            </th>
                            <th
                              className="sorting"
                              onClick={() =>
                                toggleSort("temp_emission_factor", statusIsASC)
                              }
                            >
                              <span>
                                {t("Translation.temp_emission_factor")}
                              </span>
                            </th>
                            {(canUpdateEquipment || canDeleteEquipment) && (
                              <th
                                className="sorting"
                                onClick={() =>
                                  toggleSort("actions", statusIsASC)
                                }
                              >
                                {t("Translation.actions")}
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {equipmentData ? (
                            equipmentData?.map((equipment) => (
                              <tr key={equipment.id}>
                                <td>{equipment.name}</td>
                                <td>
                                  {
                                    categoryOptions.find(
                                      (obj) => obj.value === equipment.category
                                    )?.label
                                  }
                                </td>
                                <td>{equipment.brand}</td>
                                <td>{equipment.model}</td>
                                <td>
                                  {
                                    fuelTypeOptions.find(
                                      (e) => e.value === equipment.fuelType
                                    )?.label
                                  }
                                </td>
                                <td>{equipment.emissionFactor}</td>
                                <td>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                    }}
                                  >
                                    <span>{equipment.tempEmissionFactor}</span>

                                    {!equipment.isEmissionApproved && (
                                      <div
                                        style={{ display: "flex", gap: "4px" }}
                                      >
                                        <button
                                          style={{ borderRadius: "50%" }}
                                          title={t("Translation.approve")}
                                          onClick={() =>
                                            handleApproveClick(equipment)
                                          }
                                          type="button"
                                          className="quick-approval-action-container_checkin-button"
                                        >
                                          <img src={images.ApproveCheck} />
                                        </button>

                                        <button
                                          style={{ borderRadius: "50%" }}
                                          title={t("Translation.reject")}
                                          onClick={() =>
                                            handleRejectClick(equipment)
                                          }
                                          type="button"
                                          className="quick-approval-action-container_refuse-button"
                                        >
                                          <img src={images.cancel_ic} />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </td>

                                {(canUpdateEquipment || canDeleteEquipment) && (
                                  <td>
                                    <div className="action-buttons">
                                      {canUpdateEquipment && (
                                        <button
                                          className="btn bg-transparent border-0"
                                          onClick={() => {
                                            setExistingData(equipment);
                                            setIsEquipmentModalOpen(true);
                                          }}
                                        >
                                          <img src={images.Pen} alt="edit" />
                                        </button>
                                      )}
                                      {canDeleteEquipment && (
                                        <button
                                          className="btn bg-transparent border-0"
                                          onClick={() => {
                                            setEquipmentToBeDeleted(equipment);
                                            setIsConfirmModalShow(true);
                                          }}
                                        >
                                          <img
                                            src={images.DeleteRedIcon}
                                            alt="view"
                                          />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                )}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={7} className="text-center">
                                {t("Translation.no_equipment_found")}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {equipmentData.length > 0 ? (
                    <Pagination
                      page={page}
                      onPageChange={(v) => setPage(v)}
                      perPage={perPage}
                      onPerPageChange={({ value }) => setPerPage(value)}
                      pageTotalCount={pageCount}
                    />
                  ) : (
                    ""
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default index;
